import { useTranslations } from 'use-intl';
import { useWeekReview } from '@/app/queries';
import { AiAnswerFrame, AiRefusal } from '@/ui/AiAnswer';
import { AiButton } from '@/ui/AiButton';
import { Notice } from '@/ui/Panel';

/**
 * Reading the week back, against the week that was planned.
 *
 * Observations name the figure they came from and considerations name the
 * observation they follow from, so a coach can disagree with a specific step
 * rather than with the whole answer. It may also return no considerations at
 * all: a quiet week is a real finding, and inventing advice for one is how a
 * tool stops being opened.
 */
export function AiWeekReview({
  teamId,
  from,
  to,
}: {
  teamId: string;
  from: string;
  to: string;
}) {
  const t = useTranslations();
  const review = useWeekReview();
  const answer = review.data;
  const refusal = review.error as { code?: string } | null;

  return (
    <div className="grid gap-3">
      <AiButton
        label={t('ai.reviewWeek')}
        busy={review.isPending}
        onRun={() => review.mutate({ teamId, from, to })}
      />
      {review.isPending ? <Notice>{t('ai.thinkingWeek')}</Notice> : null}
      {refusal?.code ? <AiRefusal code={refusal.code} /> : null}

      {answer ? (
        <div className="overflow-hidden rounded-(--radius-panel) border border-line bg-surface">
          <AiAnswerFrame answer={answer}>
            <div className="grid gap-4">
              <p className="max-w-[70ch] font-medium leading-relaxed">
                {answer.result.headline}
              </p>

              <section>
                <h4 className="text-meta font-semibold text-ink-muted">
                  {t('ai.observations')}
                </h4>
                <ul className="mt-1 grid gap-2">
                  {answer.result.observations.map((o) => (
                    <li key={o.text} className="border-s-2 border-line ps-3">
                      <p>{o.text}</p>
                      <p className="text-meta text-ink-faint">{o.signal}</p>
                    </li>
                  ))}
                </ul>
              </section>

              <section>
                <h4 className="text-meta font-semibold text-ink-muted">
                  {t('ai.considerations')}
                </h4>
                {answer.result.considerations.length === 0 ? (
                  <p className="mt-1 text-meta text-ink-muted">{t('ai.nothingToAct')}</p>
                ) : (
                  <ul className="mt-1 grid gap-2">
                    {answer.result.considerations.map((c) => (
                      <li key={c.text} className="border-s-2 border-signal ps-3">
                        <p>{c.text}</p>
                        <p className="text-meta text-ink-faint">{c.because}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </AiAnswerFrame>
        </div>
      ) : null}
    </div>
  );
}
